import { NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, Routes } from '@angular/router';

import { AuthGuard } from '~core/guards/auth.guard';
import { CanImpersonateGuard } from '~core/guards/can-impersonate.guard';
import { CatalogGuard } from '~core/guards/catalog.guard';
import { CustomHubGuard } from '~core/guards/custom-hub.guard';
import { StoryblokGuard } from "~core/guards/storyblok.guard";
import { BrandPLPGuard } from '~core/guards/brand-plp.guard';
import { CustomPlpGuard } from '~core/guards/custom-plp.guard';
import { PlpV2Guard } from '~core/guards/plp-v2.guard';

import {
  APP_AUTH_ROUTE,
  APP_BRAND_PAGE,
  APP_BRAND_PLP_ROUTE,
  APP_CART_ROUTE,
  APP_CATALOG_ROUTE,
  APP_CATEGORY_ROUTE,
  APP_CHECKOUT_ROUTE,
  APP_CUSTOM_HUB_ROUTE,
  APP_CUSTOM_PLP_ROUTE,
  APP_ENTREPRENEUR_ACCOUNT_ROUTE,
  APP_LANDING_PAGE_ROUTE,
  APP_LEGAL_PAGES_ROUTE,
  APP_PRODUCT_ROUTE,
  APP_SEARCH_ROUTE,
  APP_SELLER_ROUTE,
  APP_SUPPORT_ROUTE,
  BLOCKED_ACCOUNT_PAGE,
  FR_BUYER_SUPPORT_EXTERNAL_LINK,
  IMPERSONATE_ROUTE,
  NEWSLETTER_LANDING,
  NL_BUYER_SUPPORT_EXTERNAL_LINK,
  SERVER_ERROR_PAGE,
  WEEE_LANDING_PAGE,
  ACCESS_DENIED_PAGE,
  NOT_FOUND_PAGE,
  APP_HOME_ROUTE,
} from '~shared/constants';
import { NotFoundComponent } from '~shared/components/not-found/not-found.component';
import { ServerErrorPageComponent } from '~shared/components/server-error-page/server-error.component';
import { ImpersonateComponent } from '~core/components/impersonate/impersonate.component';
import {
  DE_BUYER_SUPPORT_EXTERNAL_LINK,
  ES_BUYER_SUPPORT_EXTERNAL_LINK,
  IT_BUYER_SUPPORT_EXTERNAL_LINK,
  PT_BUYER_SUPPORT_EXTERNAL_LINK,
} from '~shared/constants/links.constants';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';

import { BlockedAccountComponent } from '~shared/components/blocked-account/blocked-account.component';
import { CategoryPLPGuard } from '~core/guards/category-plp.guard';

const buyerSupportExternal = 'buyerSupportExternal';
const catalogTitle = 'TITLE.PAGE.CATALOG';
const titlePageCheckoutCart = 'TITLE.PAGE.CHECKOUT.CART';

const catalogModuleMethod = () => import('./modules/catalog/catalog.module')
  .then((m) => m.CatalogModule);

const moduleRoutes: Routes = [
  {
    path: APP_LEGAL_PAGES_ROUTE,
    loadChildren: () => import('./modules/legal/legal.module')
      .then((m) => m.LegalModule),
  },
  {
    path: APP_CATALOG_ROUTE,
    loadChildren: () => catalogModuleMethod(),
    data: { title: catalogTitle },
    canActivate: [CatalogGuard, PlpV2Guard],
  },
  {
    path: APP_CUSTOM_PLP_ROUTE,
    loadChildren: () => catalogModuleMethod(),
    canActivate: [PlpV2Guard],
    canLoad: [CustomPlpGuard],
  },
  {
    path: APP_CATEGORY_ROUTE,
    loadChildren: () => catalogModuleMethod(),
    data: { title: catalogTitle },
    canActivate: [PlpV2Guard],
    canLoad: [CategoryPLPGuard]
  },
  {
    path: APP_SEARCH_ROUTE,
    loadChildren: () => catalogModuleMethod(),
    data: { title: catalogTitle },
    canActivate: [PlpV2Guard],
  },
  {
    path: APP_CUSTOM_HUB_ROUTE,
    loadChildren: () => import('./modules/custom-hub/custom-hub.module')
      .then((m) => m.CustomHubModule),
    data: { title: 'TITLE.PAGE.CUSTOM_HUB' },
    canLoad: [CustomHubGuard],
  },
  {
    path: APP_LANDING_PAGE_ROUTE,
    loadChildren: () => import('./modules/landing-page/landing-page.module')
      .then((m) => m.LandingPageModule),
    data: { title: 'TITLE.PAGE.LANDING_PAGE' },
    canLoad: [StoryblokGuard],
  },
  {
    path: APP_BRAND_PLP_ROUTE,
    loadChildren: () => import('./modules/brand-plp/brand-plp.module')
      .then((m) => m.BrandPLPModule),
    canLoad: [BrandPLPGuard],
  },
  {
    path: APP_BRAND_PAGE,
    loadChildren: () => import('./modules/brand/brand.module')
      .then((m) => m.BrandModule),
    data: { title: 'TITLE.PAGE.DEMOZONE' },
  },
  {
    path: APP_SELLER_ROUTE,
    loadChildren: () => import('./modules/seller/seller.module')
      .then((m) => m.SellerModule),
  },
  {
    path: APP_CHECKOUT_ROUTE,
    children: [
      {
        path: '',
        redirectTo: APP_CART_ROUTE,
        pathMatch: 'prefix',
      },
      {
        path: APP_CHECKOUT_ROUTE,
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/checkout/checkout.module')
          .then((m) => m.CheckoutModule),
        data: { leaveOnLogout: true },
      },
    ],
  },
  {
    path: APP_AUTH_ROUTE,
    loadChildren: () => import('./modules/authorization/authorization.module')
      .then((m) => m.AuthorizationModule),
  },
  {
    path: APP_ENTREPRENEUR_ACCOUNT_ROUTE,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/account/account.module')
      .then((m) => m.AccountModule),
    data: { leaveOnLogout: true },
  },
  {
    path: IMPERSONATE_ROUTE,
    canActivate: [CanImpersonateGuard],
    component: ImpersonateComponent,
  },
  {
    path: APP_SUPPORT_ROUTE,
    children: [],
    canActivate: [buyerSupportExternal],
    data: {
      externalUrls: {
        de: DE_BUYER_SUPPORT_EXTERNAL_LINK,
        es: ES_BUYER_SUPPORT_EXTERNAL_LINK,
        it: IT_BUYER_SUPPORT_EXTERNAL_LINK,
        pt: PT_BUYER_SUPPORT_EXTERNAL_LINK,
        nl: NL_BUYER_SUPPORT_EXTERNAL_LINK,
        fr: FR_BUYER_SUPPORT_EXTERNAL_LINK
      },
    },
  },
  {
    path: NEWSLETTER_LANDING,
    loadChildren: () =>
      import('./modules/newsletter-landing/newsletter-landing.module')
        .then((m) => m.NewsletterLandingModule),
  },
  {
    path: SERVER_ERROR_PAGE,
    component: ServerErrorPageComponent,
    data: { title: 'TITLE.PAGE.SERVER_ERROR' },
  },
  {
    path: BLOCKED_ACCOUNT_PAGE,
    component: BlockedAccountComponent,
  },
  {
    path: WEEE_LANDING_PAGE,
    loadChildren: () =>
      import('./shared/modules/weee-landing-page/weee-landing-page.module')
        .then((m) => m.WeeeLandingPageModule),
  },
  {
    path: APP_HOME_ROUTE,
    component: NotFoundComponent,
    data: { title: 'TITLE.PAGE.NOT_FOUND' }
  },
  {
    path: NOT_FOUND_PAGE,
    component: NotFoundComponent,
    data: { title: 'TITLE.PAGE.NOT_FOUND' }
  },
  {
    path: '**',
    redirectTo: `/${NOT_FOUND_PAGE}`
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(moduleRoutes, {
      scrollPositionRestoration: 'disabled',
      anchorScrolling: 'enabled',
      initialNavigation: 'enabled',
      relativeLinkResolution: 'legacy',
    }),
  ],
  providers: [
    {
      provide: buyerSupportExternal,
      useValue: (route: ActivatedRouteSnapshot) => {
        const market = MarketDetectionUtil.getCountryCode();
        window.open((route.data as any).externalUrls[market], '_blank', 'noopener,noreferrer');
      },
    },
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
